import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { Box, Button, IconButton, Typography } from "@mui/material";
import dayjs, { Dayjs } from "dayjs";
import moment from "moment";
import "moment-timezone";
import { useMemo, useState } from "react";
import { Calendar, momentLocalizer } from "react-big-calendar";
import "react-big-calendar/lib/css/react-big-calendar.css";
import CustomSelect from "../../../../../components/CustomSelect";
import Flex from "../../../../../components/Flex";
import { useNavigate } from "react-router-dom";
import { Option } from "../../../../../constants/common";
import useCurrentUser from "../../../../../hooks/local-storage/use-current-user";
import PatientAppointmentDetailsModal from "./PatientAppointmentDetailsModal";
import { ViewType } from "../../../../../pages/app/provider/scheduling/SchedulingLayout";
import { EventType } from "../../../../../pages/app/provider/scheduling/availability/availability-contant";
import {
  SchedulingDayEventCard,
  ShowMoreDrawer,
  WeekEventCard,
  eventWrapper,
} from "../../../../../pages/app/provider/scheduling/availability/scheduling-utils";
import useDisclosure from "../../../../../hooks/use-disclosure";
import {
  DAY_VIEW_STYLES,
  WEEK_VIEW_STYLES,
  getCalcFilterDate,
  getWeekStartAndEnd,
} from "../../../../../pages/app/provider/scheduling/scheduling-constant";
import { useAppointmentsV2 } from "../../../../../queries/patient-portal/forms/appointment/use-appointment-list-v2";
import {
  getDateWithOffsetByMinusHr,
  getStartEndDateOfSameDay,
} from "../../../../../utils/date-time-utility";
import { getXtenantId } from "../../../../../utils/utils";
import { useAppointmentContext } from "../context/AppointmentContext";
import AppointmentFilters from "./AppointmentFilters";

const AppointmentCalendar: React.FC = () => {
  const { filters } = useAppointmentContext();
  const [currentDate, setCurrentDate] = useState<Dayjs>(dayjs());
  const [view, setView] = useState<"month" | "week" | "day">("week");

  const getNavigationUnit = (): "day" | "week" | "month" => {
    if (view === "day") {
      return "day";
    }
    if (view === "week") {
      return "week";
    }
    return "month";
  };

  const handlePreviousMonth = () => {
    setCurrentDate((prevDate) => {
      const updatedDate = prevDate.subtract(1, getNavigationUnit());
      calcFilterDate(view, updatedDate.toDate());
      return updatedDate;
    });
  };

  const handleNextMonth = () => {
    setCurrentDate((prevDate) => {
      const updatedDate = prevDate.add(1, getNavigationUnit());
      calcFilterDate(view, updatedDate.toDate());
      return updatedDate;
    });
  };

  const handleToday = () => {
    const today = dayjs();
    setCurrentDate(today);
    calcFilterDate(view, today.toDate());
  };

  const { endTime, startTime } = getStartEndDateOfSameDay(dayjs(new Date()));
  const { sDate, eDate } = getWeekStartAndEnd(new Date() as any);
  const [filterDate, setFilterDate] = useState<[string, string]>([
    view === "day"
      ? (startTime ?? new Date()?.toISOString())
      : sDate?.toISOString(),
    view === "day"
      ? (endTime ?? new Date()?.toISOString())
      : eDate?.toISOString(),
  ]);

  const calcFilterDate = (
    v?: ViewType,
    sDate?: Date,
    currentView: ViewType = view
  ) => {
    setFilterDate((prevDate) =>
      getCalcFilterDate(prevDate, v, sDate, currentView)
    );
  };

  const localizer = momentLocalizer(moment);
  const showMoreDisclosure = useDisclosure(false);
  const viewDetailsDisclosure = useDisclosure(false);
  const navigate = useNavigate();
  const [patient] = useCurrentUser();

  const appointment_mode_mapper: Record<string, string> = {
    Telehealth: "VIRTUAL",
    "In Person": "IN_PERSON",
  };

  const { data: appointmentsList } = useAppointmentsV2({
    pagination: { page: 0, pageSize: 250 },
    startDate: filterDate[0] ?? new Date()?.toISOString(),
    endDate: filterDate[1] ?? new Date()?.toISOString(),
    patientUuid: patient?.uuid ? [patient.uuid] : undefined,
    providerUuid:
      filters.provider === "all"
        ? undefined
        : filters.provider
          ? [filters.provider]
          : undefined,
    appointmentMode:
      filters.appointmentMode === "all"
        ? undefined
        : filters.appointmentMode
          ? [
              appointment_mode_mapper[
                filters.appointmentMode as keyof typeof appointment_mode_mapper
              ],
            ]
          : undefined,
    providerGroupId: getXtenantId(),
    disabled: !patient?.uuid,
  });
  // const mapAppointmentMode = (mode?: string): string => {
  //   switch (mode) {
  //     case "In-Person":
  //     case "IN_PERSON":
  //     case "IN-PERSON":
  //     case "IN PERSON":
  //     case "In Person":
  //       return "In Person";
  //     default:
  //       return "Telehealth";
  //   }
  // };

  const EVENT: EventType[] | undefined = useMemo(() => {
    const content = appointmentsList?.data?.content ?? [];

    return content.map((it) => ({
      title: `${it?.patientName}`,
      start: it.startTime ? new Date(it.startTime) : undefined,
      end: it?.endTime
        ? new Date(it.endTime)
        : view === "month"
          ? new Date(
              getStartEndDateOfSameDay(dayjs(it?.startTime))?.endTime as any
            )
          : null,
      status: it.status,
      mode: it?.mode,
      type: it?.type,
      patientName: it?.patientName,
      ...it,
    }));
  }, [appointmentsList?.data?.content, view]);

  return (
    <Flex
      flexDirection="column"
      width="100%"
      sx={{ backgroundColor: "#FFFFFF" }}
    >
      {/* Calendar Header Controls */}
      <Box
        sx={{
          borderBottom: 1,
          borderColor: "divider",
          paddingX: 3,
          paddingY: 0.5,
          backgroundColor: "#FFFFFF",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <Flex gap={1.5} alignItems="center" flexWrap="wrap">
          <Button variant={"outlined"} onClick={handleToday}>
            Today
          </Button>
          <Flex gap={1} alignItems="center">
            <IconButton
              onClick={handlePreviousMonth}
              size="small"
              sx={{
                color: "#1A1A1A",
                "&:hover": {
                  backgroundColor: "rgba(0,0,0,0.04)",
                },
              }}
            >
              <ChevronLeftIcon />
            </IconButton>
            <Typography
              variant="h6"
              sx={{
                fontSize: "16px",
                fontWeight: 500,
                minWidth: "120px",
                textAlign: "center",
              }}
            >
              {view === "day"
                ? currentDate.format("MMM D, YYYY")
                : view === "week"
                  ? (() => {
                      const start = currentDate.startOf("week");
                      const end = currentDate.endOf("week");
                      return start.year() === end.year()
                        ? `${start.format("MMM D")} - ${end.format("MMM D, YYYY")}`
                        : `${start.format("MMM D, YYYY")} - ${end.format("MMM D, YYYY")}`;
                    })()
                  : currentDate.format("MMMM YYYY")}
            </Typography>
            <IconButton
              onClick={handleNextMonth}
              size="small"
              sx={{
                color: "#1A1A1A",
                "&:hover": {
                  backgroundColor: "rgba(0,0,0,0.04)",
                },
              }}
            >
              <ChevronRightIcon />
            </IconButton>
          </Flex>
        </Flex>

        <Flex gap={1.5} alignItems="center" flexWrap="wrap">
          <AppointmentFilters />
          <Box width={"120px"}>
            <CustomSelect
              disableSortOptions
              clearable={false}
              options={
                [
                  { value: "day", label: "Day" },
                  { value: "week", label: "Week" },
                  { value: "month", label: "Month" },
                ] as Option[]
              }
              value={view}
              onChange={(val) => {
                calcFilterDate(val as ViewType, currentDate.toDate(), view);
                setView(val as ViewType);
              }}
            />
          </Box>
        </Flex>
      </Box>

      {/* Calendar Grid */}
      <Box
        width="100%"
        sx={{
          paddingX: 2,
          "& .rbc-event-label": { display: "none" },
          "& .rbc-header": {
            backgroundColor: "white",
            color: "#373D41",
          },
          // In month view, make each event tall enough that only the first
          // appointment fits per day cell; the rest collapse into "+X more"
          // (matches the provider scheduling calendar behaviour).
          ...(view === "month" && {
            ".rbc-event": {
              height: "56px",
              width: "96% !important",
            },
            ".rbc-show-more": {
              marginTop: "15px!important",
            },
          }),
          ...(view === "week" && WEEK_VIEW_STYLES),
          ...(view === "day" && DAY_VIEW_STYLES),
        }}
      >
        <Calendar
          // offset of 30 minutes to the current time
          scrollToTime={getDateWithOffsetByMinusHr(new Date(), 0.5)}
          enableAutoScroll
          localizer={localizer}
          toolbar={false}
          step={view === "day" || view === "week" ? 60 : 7.5}
          timeslots={view === "day" || view === "week" ? 1 : undefined}
          dayLayoutAlgorithm={
            view === "day" || view === "week" ? "no-overlap" : undefined
          }
          events={EVENT}
          key={view}
          startAccessor="start"
          endAccessor="end"
          date={currentDate.toDate()}
          view={view}
          onShowMore={(events) => showMoreDisclosure.open(events)}
          onSelectEvent={(event: any) => {
            if (!event?.uuid) return;
            viewDetailsDisclosure.open(event);
          }}
          onNavigate={(date) => {
            setCurrentDate(dayjs(date));
            calcFilterDate(view, date);
          }}
          eventPropGetter={() => {
            if (view === "day") {
              return {
                style: {
                  backgroundColor: "transparent",
                  borderRadius: 8,
                  color: "black",
                  maxWidth: "96%",
                  marginLeft: "2%",
                  marginBottom: "4px",
                  minHeight: "21px",
                  border: "none",
                  boxShadow: "none",
                  padding: 0,
                },
              };
            }
            return {
              style: {
                backgroundColor: "#F3F4F6",
                borderRadius: 6,
                color: "black",
                width: "96%",
                marginLeft: "2%",
                marginBottom: "2px",
                minHeight: "21px",
              },
            };
          }}
          style={{
            height: "calc(100vh - 175px)",
            fontFamily: "'Inter', sans-serif",
          }}
          components={{
            event: (props) =>
              view === "day" ? (
                <SchedulingDayEventCard props={props} />
              ) : view === "week" ? (
                <WeekEventCard props={props} />
              ) : (
                eventWrapper(props, view, false)
              ),
          }}
        />
      </Box>

      <ShowMoreDrawer
        disclosureHost={showMoreDisclosure}
        onEvenClick={(event: any) => {
          showMoreDisclosure.close();
          if (!event?.uuid) return;
          viewDetailsDisclosure.open(event);
        }}
        isMultipleProviders={false}
      />

      <PatientAppointmentDetailsModal
        open={viewDetailsDisclosure.opened}
        onClose={viewDetailsDisclosure.close}
        appointment={viewDetailsDisclosure.data}
        onCancel={viewDetailsDisclosure.close}
        onJoin={(link) => {
          viewDetailsDisclosure.close();
          navigate(`/app/preview?room=${link?.split("room=")?.[1]}`);
        }}
      />
    </Flex>
  );
};

export default AppointmentCalendar;
